import { ActionOrigin, AnalyticsService } from 'base/analytics';
import {
  BLOCKS_MARKER_MIME_TYPE,
  isSafari,
  writeToClipboard,
} from 'base/clipboard';
import { exists, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { extractSimpleElementState } from 'base/react_integrations';
import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { ColoredIcon } from 'base/ui/colored_icon';
import { Dialog, DialogHeroImage } from 'base/ui/dialog';
import {
  IconCode,
  IconCopyAi,
  IconFigma,
  IconPageAi,
  IconReact,
  IconShuffleAi,
  IconSitemapAi,
  IconStars,
  IconWebflow,
} from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import {
  AnyMenuItem,
  menuDivider,
  menuItem,
  menuText,
  subMenu,
} from 'base/ui/menu_model';
import { TickRow } from 'base/ui/tick_row';
import { ToastQueue } from 'base/ui/toast';
import { TextRegularLight, Title5 } from 'base/ui/typography';
import { isTextEditingSelection } from 'blocks/component_renderer/interactable';
import { startTextRewritingAi } from 'blocks/component_renderer/text_overrides';
import { isMac } from 'blocks/platform';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import {
  isInputElement,
  shortcut,
  useKeyboardShortcuts,
} from '../keyboard_shortcuts';
import {
  ClipboardPayload,
  copyElement,
  copyPage,
  copySection,
  tryReadClipboardApi,
  tryReadClipboardEvent,
} from './clipboard';
import { ProjectEditingActions } from './editing_actions/project';
import {
  EditorMode,
  editorModeStore,
  isWireframeOrDesign,
} from './editor_mode';
import ExportFigmaPasteImage from './export-figma-paste.file.webp';
import ExportWebflowPasteImage from './export-webflow-paste.file.webp';
import {
  exportFormat,
  exportFormatMenuItems,
  ExportHtmlDialog,
  ExportReactDialog,
  hasDismissedHTMLExportInstructions,
  hasDismissedReactExportInstructions,
} from './export_and_share';
import {
  canExport,
  downloadArchive,
  downloadTextFile,
  exportPageSource,
  exportSectionSource,
  showMissingComponentsWarning,
  slugToComponentName,
} from './export_react_source';
import { ElementInstance, SectionRef, Space } from './model';
import {
  findThreadsInProject,
  removeThreadsFromElement,
  removeThreadsFromPage,
  removeThreadsFromSection,
} from './model_comment_helpers';
import {
  shallowCloneElementState,
  shallowClonePageState,
  shallowCloneSectionRefState,
  unrefSection,
} from './model_helpers';
import { WebflowSafariWarningDialog } from './safari-warning';
import { SelectionState } from './selection/states';
import { SelectionStore } from './selection/store';
import styles from './selection_actions.css';
import { ProjectSession } from './session/session';
import { createStyleGuideSelectionDialog } from './style_guide_selection_dialog';

const CLIPBOARD_TOASTS_GROUP = Symbol('clipboard toasts');

export interface SelectionActions {
  delete?: ((origin: ActionOrigin) => void) | string;
  duplicate?: ((origin: ActionOrigin) => void) | string;
  copy?: (origin: ActionOrigin) => void;
  copyExportFigma?: (origin: ActionOrigin) => void;
  copyExportWebflow?: (origin: ActionOrigin) => void;
  copySource?:
    | ((origin: ActionOrigin) => void)
    | { disabled: boolean; message: string };
  downloadSource?:
    | ((origin: ActionOrigin) => void)
    | { disabled: boolean; message: string };
  copySimpleElementState?: (origin: ActionOrigin) => void;
  cut?: (origin: ActionOrigin) => void;
  paste?: (payload: ClipboardPayload, origin: ActionOrigin) => void;
  pasteViaMenu?: () => void;
  showCopyExportOptions: boolean;
  maybePasteViaEvent: (e: ClipboardEvent) => void;
  editing?: ProjectEditingActions;

  // TODO(george): these shouldn't be in the interface... they're just there to
  // get ElementSelectionActionsImpl to work for now.
  toastQueue?: ToastQueue;
  analytics?: AnalyticsService;
  library?: LibraryCache;
  session?: ProjectSession;
}

export class SelectionActionsImpl implements SelectionActions {
  constructor(
    readonly selection: SelectionStore,
    readonly toastQueue: ToastQueue,
    readonly editing: ProjectEditingActions | undefined,
    readonly analytics: AnalyticsService,
    readonly library: LibraryCache,
    readonly session: ProjectSession,
  ) {
    makeObservable(this);
  }

  @observable.ref
  showFigmaPasteHowTo = false;
  @observable.ref
  showWebflowPasteHowTo = false;
  @observable.ref
  showWebflowSafariWarning = false;
  @observable.ref
  showPasteViaShortcutsHowTo = false;
  @observable.ref
  showReactExportHowTo = false;
  @observable.ref
  showHtmlExportHowTo = false;

  styleGuideSelectionDialog = createStyleGuideSelectionDialog();

  @computed
  get sel() {
    return this.selection.get();
  }

  @computed
  get delete() {
    const { editing, sel } = this;
    if (!editing) return;
    if (sel.element) return editing.forElementNode(sel).delete;
    if (sel.section) return editing.forSection(sel.section).deleteWithConfirm;
    if (sel.page) return editing.forPage(sel.page).deleteSelfAndSubPages;
    if (sel.space) return editing.forSpace(sel.space).deleteWithConfirm;
  }

  trackDeleteFailed(origin: ActionOrigin) {
    const error = this.delete;
    if (typeof error !== 'string' || !this.editing) return;
    const { sel } = this;
    const context = sel.element
      ? this.editing.forElementNode(sel).analyticsContext
      : {
          sectionRootSlug: '',
          slotName: '',
          elementSlug: '',
          elementRootName: '',
          elementCanonical: '',
        };
    this.analytics.track({
      event: 'blocks/delete-failed',
      error,
      ...context,
      origin,
    });
  }

  @computed
  get duplicate() {
    const { editing } = this;
    if (!editing) return;
    const { sel } = this;
    if (sel.element) return editing.forElementNode(sel).duplicate;
    if (sel.section) return editing.forSection(sel.section).duplicate;
    if (sel.page) return editing.forPage(sel.page).duplicate;
    if (sel.space) return editing.forSpace(sel.space).duplicate;
  }

  @computed
  get copy() {
    return this.makeCopyOrCut(undefined);
  }

  @computed
  get cut() {
    return this.editing ? this.makeCopyOrCut(this.editing) : undefined;
  }

  private showExportHelpMessage = () => {
    if (
      exportFormat.value === 'react' &&
      !hasDismissedReactExportInstructions()
    ) {
      this.showReactExportHowTo = true;
    } else if (
      exportFormat.value === 'html' &&
      !hasDismissedHTMLExportInstructions()
    ) {
      this.showHtmlExportHowTo = true;
    }
  };

  @computed
  get downloadSourceState() {
    const { session } = this;
    if (!this.hasComponentsToCopyExport) {
      return {
        disabled: true,
        message: 'You need to add a component before downloading',
      };
    }
    const format = exportFormat.value;
    const rootElement =
      this.sel.section &&
      unrefSection(this.sel.section, this.session.project)?.element.current;
    const page = this.sel.page;
    if (!this.editing || !(format === 'react' || format === 'html')) {
      return;
    }
    if (rootElement != null && !canExport(rootElement.source.slug)) {
      return {
        disabled: true,
        message: 'Component currently not available in this format',
      };
    }
    return { disabled: false };
  }

  @action
  downloadSource = (origin: ActionOrigin) => {
    const state = this.downloadSourceState;
    if (!state || state.disabled) {
      return state;
    }
    const format = exportFormat.value;
    const rootElement =
      this.sel.section &&
      unrefSection(this.sel.section, this.session.project)?.element.current;
    const page = this.sel.page;
    if (!this.editing || !(format === 'react' || format === 'html')) {
      return;
    }
    if (rootElement != null) {
      this.analytics.track({
        event: 'blocks/click-export',
        destination: exportFormat.value,
        method: 'download',
        subject: 'section',
        componentSlugs: getComponentSlugsOfSelection(this.sel, this.selection),
        origin,
      });
      this.analytics.track({
        event: 'blocks/download',
        format,
        subject: 'section',
        origin,
      });
      if (!this.session.productRestrictions.webflowExportEnabled) {
        this.session.showUpgradeWall?.('export');
        return;
      }
      exportSectionSource(format, rootElement.state, this.library).then(
        async (content) => {
          if (content == null) {
            this.toastQueue.push({
              label: 'Component currently not available in this format',
              group: CLIPBOARD_TOASTS_GROUP,
            });
            return;
          }
          this.showExportHelpMessage();
          await downloadTextFile(
            `${slugToComponentName(rootElement.source.slug)}.${
              format === 'react' ? 'jsx' : 'html'
            }`,
            content,
          );
        },
      );
    } else if (page) {
      this.analytics.track({
        event: 'blocks/click-export',
        destination: exportFormat.value,
        method: 'download',
        subject: 'page',
        componentSlugs: getComponentSlugsOfSelection(this.sel, this.selection),
        origin,
      });
      this.analytics.track({
        event: 'blocks/download',
        format,
        subject: 'page',
        origin,
      });
      if (!this.session.productRestrictions.webflowExportEnabled) {
        this.session.showUpgradeWall?.('export');
        return;
      }
      exportPageSource(format, page, this.library, this.session.project).then(
        async ({ files, componentsMissing }) => {
          if (componentsMissing) {
            const abort = await new Promise((resolve) =>
              showMissingComponentsWarning({
                onConfirm: () => resolve(false),
                onCancel: () => resolve(true),
              }),
            );
            if (abort) {
              return;
            }
          }
          this.showExportHelpMessage();
          if (files.length === 1) {
            downloadTextFile(files[0].name, files[0].input);
          } else {
            downloadArchive(
              files,
              page.name.toLowerCase().replaceAll(' ', '-'),
            );
          }
        },
      );
    }
  };

  /**
   * Performs checks on the project to ensure that a Webflow style guide has
   * been selected before copying to Webflow via the clipboard.
   *
   * Returns true if copying can proceed (e.g. there was already a style guide
   * selected, or they're exporting to something else like Figma), false if it
   * should be aborted (e.g. we prompted to select a style guide and the user
   * closed the dialog without making a selection).
   */
  private async checkWebflowStyleGuide(): Promise<boolean> {
    if (exportFormat.value !== 'webflow') {
      // Nothing to check if we're not exporting to Webflow
      return true;
    }

    if (this.session.project.webflowStyle != null) {
      // Project has a Webflow style guide selected, all good
      return true;
    }

    const projectCreatedAtDate = new Date(this.session.createdAt);
    if (projectCreatedAtDate >= new Date('2025-03-04T09:00:00+11:00')) {
      // We only show the style guide selection prompt for projects created
      // before we shipped SGB export and the V3 style guide.
      return true;
    }

    const newWebflowStyle = await this.styleGuideSelectionDialog.showDialog();
    if (newWebflowStyle == null) {
      return false;
    }
    this.session.project.webflowStyle = newWebflowStyle;
    return true;
  }

  private makeCopyOrCut(cutUsing: ProjectEditingActions | undefined) {
    if (!this.editing) {
      return;
    }
    const { sel } = this;
    const canExportSelection =
      sel.page != null &&
      this.session.productRestrictions.webflowExportEnabled &&
      this.session.canViewWireframe(sel.page);

    if (sel.element) {
      return this.withCopyToasts(async (origin) => {
        const hasWebflowStyleGuide = await this.checkWebflowStyleGuide();
        if (!hasWebflowStyleGuide) {
          return false;
        }

        copyElement(
          this.selection.project.state,
          sel.element.state,
          this.library,
          canExportSelection,
        );
        const del = cutUsing?.forElementNode(sel)?.delete;
        if (typeof del === 'function') {
          del(origin);
        }
        return true;
      });
    }
    if (sel.section) {
      return this.withCopyToasts(async (origin) => {
        const hasWebflowStyleGuide = await this.checkWebflowStyleGuide();
        if (!hasWebflowStyleGuide) {
          return false;
        }

        await copySection(
          this.selection.project.state,
          sel.section.state,
          this.library,
          canExportSelection,
        );
        cutUsing?.forSection(sel.section).deleteWithConfirm();
        this.analytics.track({
          event: 'blocks/click-export',
          destination: exportFormat.value,
          method: 'copy',
          subject: 'section',
          componentSlugs: getComponentSlugsOfSelection(
            this.sel,
            this.selection,
          ),
          origin,
        });
        this.analytics.track({
          event: 'blocks/copy',
          object: 'section',
          numPages: 0,
          numSections: 1,
          isCut: cutUsing != null,
          format: exportFormat.value,
          origin,
        });
        return true;
      });
    }
    if (sel.page) {
      return this.withCopyToasts(async (origin) => {
        const hasWebflowStyleGuide = await this.checkWebflowStyleGuide();
        if (!hasWebflowStyleGuide) {
          return false;
        }

        await copyPage(
          this.selection.project,
          sel.page,
          this.library,
          canExportSelection,
        );
        cutUsing?.forPage(sel.page).deleteSelfOnly?.();
        this.analytics.track({
          event: 'blocks/click-export',
          destination: exportFormat.value,
          method: 'copy',
          subject: 'page',
          componentSlugs: getComponentSlugsOfSelection(
            this.sel,
            this.selection,
          ),
          origin,
        });
        this.analytics.track({
          event: 'blocks/copy',
          object: 'page',
          numPages: 1,
          numSections: sel.page.sections.length,
          isCut: cutUsing != null,
          format: exportFormat.value,
          origin,
        });
        return true;
      });
    }
  }

  private withCopyToasts(
    action: (origin: ActionOrigin) => Promise<boolean> | boolean,
  ): (origin: ActionOrigin) => Promise<void> {
    return async (origin: ActionOrigin) => {
      try {
        const copied = await action(origin);
        if (copied) {
          this.toastQueue.push({
            label: 'Copied to clipboard',
            group: CLIPBOARD_TOASTS_GROUP,
          });
        }
      } catch (e) {
        console.error('Error copying to clipboard', e);
        this.toastQueue.push({
          label: 'Error copying to clipboard',
          group: CLIPBOARD_TOASTS_GROUP,
        });
      }
    };
  }

  readonly paste = this.editing
    ? (payload: ClipboardPayload, origin: ActionOrigin) => {
        const { editing } = this;
        if (!editing) throw new Error(); // precondition
        const { sel } = this;
        const { project } = this.selection;
        const selSpace = sel.space || 'home';
        const selPage =
          sel.page ||
          (selSpace !== 'home' && selSpace.subPages[0]) ||
          project.homePage;

        const pageEditing = editing.forPage(selPage);

        // We remove any references to existing comment threads on PASTE.  Why?
        //   - If you CUT then PASTE a section, the threads should move
        //   - If you CUT then PASTE 2x, the threads should move to the first
        //     paste location.  (If the thread ids were preserved, it'd move
        //     to the last paste location in document order - unpredictable.)
        //   - If you COPY then PASTE, the comment threads never move
        const existingThreadIds = new Set(findThreadsInProject(project).keys());

        switch (payload.type) {
          case 'page':
            editing.mergeGlobalSections(payload.globalSections);
            pageEditing.addPageNatural(
              shallowClonePageState(
                removeThreadsFromPage(payload.state, existingThreadIds),
              ),
            );
            break;
          case 'section-ref':
            editing.mergeGlobalSections(payload.globalSections);
            pageEditing.insertSection(
              shallowCloneSectionRefState(
                removeThreadsFromSection(payload.state, existingThreadIds),
              ),
              sel.section,
            );
            break;
          case 'element':
            const done = sel.element
              ? editing
                  .forElementNode(sel)
                  .pasteElement(
                    shallowCloneElementState(
                      removeThreadsFromElement(
                        payload.state,
                        existingThreadIds,
                      ),
                    ),
                    origin,
                  )
              : Promise.resolve("You can't paste an element here");
            done.then((error) => {
              error &&
                this.toastQueue.push({
                  label: error,
                  group: CLIPBOARD_TOASTS_GROUP,
                });
            });
            break;
          default:
            throw new UnreachableError(payload);
        }
      }
    : undefined;

  readonly pasteViaMenu = this.editing
    ? async () => {
        const payload = await tryReadClipboardApi(
          (label) =>
            this.toastQueue.push({ label, group: CLIPBOARD_TOASTS_GROUP }),
          () => (this.showPasteViaShortcutsHowTo = true),
        );
        if (!payload) return;
        this.paste!(payload, ActionOrigin.ContextMenu);
      }
    : undefined;

  readonly maybePasteViaEvent = async (e: ClipboardEvent) => {
    if (!this.paste || !e.clipboardData) return;
    // We usually let you paste into an input, unless you're pasting blocks content
    const hasBlocksContent = e.clipboardData?.types.includes(
      BLOCKS_MARKER_MIME_TYPE,
    );
    if (isInputElement(e.target) && !hasBlocksContent) return;
    e.preventDefault();

    const payload = await tryReadClipboardEvent(e.clipboardData, (label) =>
      this.toastQueue.push({ label, group: CLIPBOARD_TOASTS_GROUP }),
    );
    if (!payload) return;
    // Assuming here that a paste event is always from the keyboard. Could
    // technically be from a browser menu though.
    this.paste(payload, ActionOrigin.Keyboard);
  };

  @computed
  get showCopyExportOptions(): boolean {
    if (!isWireframeOrDesign(editorModeStore.get())) return false;
    const { sel } = this;
    return sel.nodeId == null && sel.page != null;
  }

  @computed
  private get hasComponentsToCopyExport() {
    const hasComponent = (sref: SectionRef) => {
      const section =
        sref.type === 'inline'
          ? sref.value
          : this.selection.project.globalSections.get(sref.id);
      return section?.element.current != null;
    };

    const { sel } = this;
    if (sel.section) return hasComponent(sel.section);
    if (sel.page) return sel.page.sections.some(hasComponent);
    return false;
  }

  @computed
  get copyExportFigma() {
    const { copy, hasComponentsToCopyExport, session } = this;
    if (!hasComponentsToCopyExport || !copy) return;
    return (origin: ActionOrigin) => {
      this.analytics.track({
        event: 'blocks/click-export',
        destination: 'figma',
        method: 'copy',
        componentSlugs: getComponentSlugsOfSelection(this.sel, this.selection),
        origin,
      });
      if (!session.productRestrictions.figmaExportEnabled) {
        session.showUpgradeWall?.('export');
        return;
      }
      copy(origin);
      if (!hasDismissedFigmaPasteHowTo()) {
        this.showFigmaPasteHowTo = true;
      }
    };
  }

  @computed
  get copyExportWebflow() {
    const { copy, hasComponentsToCopyExport, session } = this;
    if (!hasComponentsToCopyExport || !copy) return;
    return async (origin: ActionOrigin) => {
      const hasWebflowStyleGuide = await this.checkWebflowStyleGuide();
      if (!hasWebflowStyleGuide) {
        return;
      }

      this.analytics.track({
        event: 'blocks/click-export',
        destination: 'webflow',
        method: 'copy',
        componentSlugs: getComponentSlugsOfSelection(this.sel, this.selection),
        origin,
      });
      if (!session.productRestrictions.webflowExportEnabled) {
        session.showUpgradeWall?.('export');
        return;
      }
      if (isSafari()) {
        this.showWebflowSafariWarning = true;
        return;
      }
      copy(origin);
      if (!hasDismissedWebflowPasteHowTo()) {
        this.showWebflowPasteHowTo = true;
      }
    };
  }

  @computed
  get copySource() {
    const { copy, hasComponentsToCopyExport, session } = this;
    if (!hasComponentsToCopyExport || !copy) return;
    if (exportFormat.value === 'html' && this.sel.page == null) return;
    if (exportFormat.value === 'react') {
      if (this.sel.section == null) {
        return;
      }
      const section =
        this.sel.section.type === 'inline'
          ? this.sel.section.value
          : this.session.project.globalSections.get(this.sel.section.id);
      if (!canExport(section?.element.current?.source.slug ?? '')) {
        return {
          disabled: true,
          message: 'Component currently not available in this format',
        };
      }
    }
    return (origin: ActionOrigin) => {
      if (!session.productRestrictions.webflowExportEnabled) {
        session.showUpgradeWall?.('export');
        return;
      }
      copy(origin);
      this.showExportHelpMessage();
    };
  }

  @computed
  get copySimpleElementState() {
    return this.withCopyToasts(async () => {
      const { sel } = this;
      if (!sel.section) {
        throw new Error('Expected selection to contain a section');
      }

      const section = unrefSection(sel.section, this.selection.project);
      if (!section) {
        throw new Error("Couldn't resolve section");
      }

      const sectionState = section?.state;
      if (!sectionState.element.current) {
        throw new Error('No element found');
      }

      const payload = await extractSimpleElementState(
        sectionState.element.current,
        this.library,
      );
      writeToClipboard({
        'text/plain': JSON.stringify(payload, undefined, 2),
      });
      return true;
    });
  }
}

export class ElementSelectionActionsImpl extends SelectionActionsImpl {
  constructor(
    readonly selection: SelectionStore,
    readonly self: SelectionState & { element: ElementInstance },
    readonly toastQueue: ToastQueue,
    readonly editing: ProjectEditingActions | undefined,
    readonly analytics: AnalyticsService,
    readonly library: LibraryCache,
    readonly session: ProjectSession,
  ) {
    super(selection, toastQueue, editing, analytics, library, session);
  }

  get sel() {
    return this.self;
  }
}

function getComponentSlugsOfSelection(
  sel: SelectionState,
  selection: SelectionStore,
): string[] {
  const srefs = sel.section ? [sel.section] : sel.page?.sections || [];
  return srefs
    .map((sref) => {
      const section =
        sref.type === 'inline'
          ? sref.value
          : selection.project.globalSections.get(sref.id);
      return section?.element.current?.source.slug;
    })
    .filter(exists);
}

function hasDismissedFigmaPasteHowTo() {
  return localStorage.getItem('dismissedFigmaPasteHowTo') === 'true';
}

function setDismissedFigmaPasteHowTo() {
  localStorage.setItem('dismissedFigmaPasteHowTo', 'true');
}

function hasDismissedWebflowPasteHowTo() {
  return localStorage.getItem('dismissedWebflowPasteHowTo') === 'true';
}

function setDismissedWebflowPasteHowTo() {
  localStorage.setItem('dismissedWebflowPasteHowTo', 'true');
}

const DELETE_TOASTS_GROUP = Symbol('delete toasts');

export function useSelectionActionShortcuts(actions: SelectionActionsImpl) {
  const del = actions.delete;
  const deleteOrToast = () => {
    if (typeof del === 'function') {
      del(ActionOrigin.Keyboard);
    } else {
      actions.toastQueue.push({
        label: del || "You can't delete this",
        group: DELETE_TOASTS_GROUP,
      });
      actions.trackDeleteFailed(ActionOrigin.Keyboard);
    }
  };
  const duplicate = actions.duplicate;
  const duplicateOrToast = () => {
    if (typeof duplicate === 'function') {
      duplicate(ActionOrigin.Keyboard);
    } else {
      actions.toastQueue.push({
        label: duplicate || "You can't duplicate this",
        group: DELETE_TOASTS_GROUP,
      });
    }
  };
  const { copy, cut, downloadSource, downloadSourceState, toastQueue } =
    actions;
  useKeyboardShortcuts(true, {
    // Always have a handler, to prevent default (cmd-d = create bookmark in many browsers)
    [shortcut('D', { cmd: true })]: duplicateOrToast,
    [shortcut('Backspace')]: deleteOrToast,
    [shortcut('Delete')]: deleteOrToast,
    [shortcut('C', { cmd: true })]: copy
      ? () => copy(ActionOrigin.Keyboard)
      : undefined,
    [shortcut('X', { cmd: true })]: cut
      ? () => cut(ActionOrigin.Keyboard)
      : undefined,
    [shortcut('E', { shift: true, cmd: true })]: () => {
      const state = downloadSourceState;
      if (!state?.disabled) {
        downloadSource(ActionOrigin.Keyboard);
      } else if (state?.message) {
        toastQueue.push({ label: state.message });
      }
    },
  });

  React.useEffect(
    () => {
      document.addEventListener('paste', actions.maybePasteViaEvent);
      return () =>
        document.removeEventListener('paste', actions.maybePasteViaEvent);
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions.paste],
  );
}

// TODO(meredithl): surface separate menus for different pageTypes.
export const selectionActionsMenu = computedFn(
  (actions: SelectionActions, askAiMenu: AnyMenuItem[]): AnyMenuItem[] => {
    const {
      duplicate,
      delete: del,
      copy,
      cut,
      copyExportFigma,
      copyExportWebflow,
      copySource,
      downloadSource,
    } = actions;

    function getExportMenuItems(): AnyMenuItem[] {
      const menuExportFormat = subMenu({
        label: 'Export format',
        menu: [
          menuText({ label: 'Export format', size: 'tiny' }),
          ...exportFormatMenuItems.get(),
        ],
      });

      if (exportFormat.value === 'webflow') {
        return [
          menuExportFormat,
          menuItem({
            icon: IconWebflow,
            label: 'Copy to Webflow',
            shortcut: '⌘C',
            disabled: typeof copyExportWebflow !== 'function',
            tooltip:
              typeof copyExportWebflow !== 'function'
                ? 'You need to add a component before copying'
                : undefined,
            onClick:
              typeof copyExportWebflow === 'function'
                ? () => copyExportWebflow(ActionOrigin.ContextMenu)
                : undefined,
          }),
        ];
      } else if (exportFormat.value === 'figma') {
        return [
          menuExportFormat,
          menuItem({
            icon: IconFigma,
            label: 'Copy to Figma',
            shortcut: '⌘C',
            disabled: typeof copyExportFigma !== 'function',
            tooltip:
              typeof copyExportFigma !== 'function'
                ? 'You need to add a component before copying'
                : undefined,
            onClick:
              typeof copyExportFigma === 'function'
                ? () => copyExportFigma(ActionOrigin.ContextMenu)
                : undefined,
          }),
        ];
      } else if (exportFormat.isSourceCodeExport) {
        return [
          menuExportFormat,
          copySource
            ? menuItem({
                icon: exportFormat.value === 'react' ? IconReact : IconCode,
                label:
                  exportFormat.value === 'react'
                    ? 'Copy React code'
                    : 'Copy HTML code',
                shortcut: '⌘C',
                onClick:
                  typeof copySource === 'function'
                    ? () => copySource(ActionOrigin.ContextMenu)
                    : undefined,
                disabled:
                  typeof copySource !== 'function'
                    ? copySource.disabled
                    : undefined,
                tooltip:
                  typeof copySource !== 'function'
                    ? copySource.message
                    : undefined,
              })
            : undefined,
          downloadSource
            ? menuItem({
                icon: exportFormat.value === 'react' ? IconReact : IconCode,
                label: `Download ${
                  exportFormat.value === 'react' ? 'React' : 'HTML'
                } code`,
                shortcut: '⇧⌘E',
                onClick:
                  typeof downloadSource === 'function'
                    ? () => downloadSource(ActionOrigin.ContextMenu)
                    : undefined,
                disabled:
                  typeof downloadSource !== 'function'
                    ? downloadSource.disabled
                    : undefined,
                tooltip:
                  typeof downloadSource !== 'function'
                    ? downloadSource.message
                    : undefined,
              })
            : undefined,
        ].filter(exists);
      }
      return [];
    }

    return [
      ...askAiMenu,
      menuItem({
        onClick:
          typeof duplicate === 'function'
            ? () => duplicate(ActionOrigin.ContextMenu)
            : undefined,
        disabled: typeof duplicate !== 'function',
        disabledReason: typeof duplicate === 'string' ? duplicate : undefined,
        label: 'Duplicate',
        shortcut: '⌘D',
      }),
      menuItem({
        onClick:
          typeof del === 'function'
            ? () => del(ActionOrigin.ContextMenu)
            : undefined,
        disabled: typeof del !== 'function',
        disabledReason: typeof del === 'string' ? del : undefined,
        label: 'Delete',
        shortcut: '⌫',
      }),
      menuItem({
        onClick:
          typeof copy === 'function'
            ? () => copy(ActionOrigin.ContextMenu)
            : undefined,
        disabled: typeof copy !== 'function',
        label: 'Copy',
        shortcut: '⌘C',
      }),
      menuItem({
        onClick:
          typeof cut === 'function'
            ? () => cut(ActionOrigin.ContextMenu)
            : undefined,
        disabled: typeof cut !== 'function',
        label: 'Cut',
        shortcut: '⌘X',
      }),
      menuItem({
        onClick: actions.pasteViaMenu,
        disabled: actions.pasteViaMenu == null,
        label: 'Paste',
        shortcut: '⌘V',
      }),
      ...(actions.showCopyExportOptions
        ? [menuDivider(), ...getExportMenuItems()]
        : []),
    ].filter(exists);
  },
);

export const pageAskAiMenu = computedFn(
  (editing: ProjectEditingActions | undefined): AnyMenuItem[] => {
    if (!editing) return [];
    return [
      subMenu({
        label: 'Ask AI',
        icon: <ColoredIcon icon={IconStars} color="colorPurple" />,
        menu: askAiMenuContents(editing, editing.selection.get()),
      }),
      menuDivider(),
    ];
  },
  { name: 'pageAskAiMenu' },
);

export const elementAskAiMenu = computedFn(
  (
    editing: ProjectEditingActions | undefined,
    sref: SectionRef,
    element: ElementInstance,
    nodeId: string | undefined,
  ) => {
    if (!editing) return [];
    return [
      subMenu({
        label: 'Ask AI',
        icon: <ColoredIcon icon={IconStars} color="colorPurple" />,
        menu: askAiMenuContents(editing, {
          ...editing.selection.factory.forSection(sref),
          element,
          nodeId,
        }),
      }),
      menuDivider(),
    ];
  },
  { name: 'elementAskAiMenu' },
);

const sitemapAiActions = (
  editing: ProjectEditingActions,
  space: Space | 'home' | undefined,
) => {
  const { selection } = editing;
  const spaceMenu = [
    menuItem({
      label: 'Edit sitemap prompt',
      icon: <ColoredIcon icon={IconStars} color="colorPurple" />,
      onClick: () => {
        selection.set({ space: space || 'home' });
        editing.sidebarNavigation.goto({ path: 'edit' });
      },
    }),
    menuDivider(),
    menuItem({
      label: 'Generate new sitemap',
      onClick: () => {
        editing.addSpaceAfter(space ?? 'home');
        editing.sidebarNavigation.goto({ path: 'edit' });
      },
      icon: <ColoredIcon icon={IconSitemapAi} color="colorPurple" />,
    }),
    menuItem({
      label: 'Generate new page',
      onClick: () => {
        editing.addPageNatural();
        editing.sidebarNavigation.goto({ path: 'edit' });
      },
      icon: <ColoredIcon icon={IconPageAi} color="colorPurple" />,
    }),
  ];
  return spaceMenu;
};

const askAiMenuContents = (
  editing: ProjectEditingActions,
  sel: SelectionState,
): AnyMenuItem[] => {
  // Space selection - either wireframe or sitemap mode
  const { selection } = editing;
  const spaceMenu = sitemapAiActions(editing, sel.space);
  if (sel.page == null) {
    return spaceMenu;
  }

  // Page selection - sitemap mode
  // (deeper selections aren't possible in sitemap mode)
  const pageEditing = editing.forPage(sel.page);
  const editPagePromptItem =
    sel.page.sections.length === 0
      ? menuItem({
          label: 'Generate page',
          icon: <ColoredIcon icon={IconPageAi} color="colorPurple" />,
          onClick: pageEditing.regenerateOrOpenForm,
        })
      : menuItem({
          label: 'Edit page prompt',
          icon: <ColoredIcon icon={IconStars} color="colorPurple" />,
          onClick: () => {
            selection.set({ space: sel.space, page: sel.page });
            editing.sidebarNavigation.goto({ path: 'edit' });
          },
        });

  if (editorModeStore.get() === EditorMode.Sitemap) {
    return [editPagePromptItem, ...spaceMenu];
  }

  // Page selection - wireframe mode
  if (sel.section == null) {
    return [
      menuItem({
        label: 'Shuffle components',
        icon: <ColoredIcon icon={IconShuffleAi} color="colorPurple" />,
        onClick: pageEditing.shuffleComponentsOrOpenForm,
        disabled: sel.page.sections.length === 0,
        disabledReason: 'The page is empty',
      }),
      menuItem({
        label: 'Generate copy',
        icon: <ColoredIcon icon={IconCopyAi} color="colorPurple" />,
        onClick: pageEditing.generateCopyOrOpenForm,
        disabled: sel.page.sections.length === 0,
        disabledReason: 'The page is empty',
      }),
      menuDivider(),
      editPagePromptItem,
      ...spaceMenu,
    ];
  }

  // Section selection - wireframe mode only
  const sectionEditing = editing.forSection(sel.section);
  const sectionFooter = [editPagePromptItem, ...spaceMenu];
  if (sel.element == null) {
    return [
      menuItem({
        label: 'Replace component',
        icon: <ColoredIcon icon={IconShuffleAi} color="colorPurple" />,
        onClick: () => {
          selection.set({
            space: sel.space,
            page: sel.page,
            section: sel.section,
          });
          editing.sidebarNavigation.goto({
            path: 'edit/replace-component',
          });
        },
      }),
      menuItem({
        label: 'Generate copy',
        icon: <ColoredIcon icon={IconCopyAi} color="colorPurple" />,
        onClick: sectionEditing.generateCopyOrOpenForm,
      }),
      menuDivider(),
      ...sectionFooter,
    ];
  }

  // Element selection - wireframe mode only
  if (isTextEditingSelection(sel, selection.project, editing.library)) {
    return [
      menuItem({
        label: 'Ask AI to write...',
        icon: <ColoredIcon icon={IconCopyAi} color="colorPurple" />,
        onClick: () => startTextRewritingAi(sel),
      }),
      menuDivider(),
      ...sectionFooter,
    ];
  } else {
    return sectionFooter;
  }
};

/**
 * Paths are only visible so able to be edited in the sitemap mode, so
 * we have a separate menu item for them, as they have fewer ai editing
 * options.
 */
export function pathAskAiMenu(
  editing: ProjectEditingActions | undefined,
): AnyMenuItem[] {
  if (!editing || editorModeStore.get() !== EditorMode.Sitemap) return [];
  return [
    subMenu({
      label: 'Ask AI',
      icon: <ColoredIcon icon={IconStars} color="colorPurple" />,
      menu: sitemapAiActions(editing, editing.selection.get().space),
    }),
    menuDivider(),
  ];
}

export const SelectionActionsDialogs = observer(
  function SelectionActionsDialogs({
    selectionActions,
  }: {
    selectionActions: SelectionActionsImpl;
  }) {
    const showCopyConfirmation = () => {
      selectionActions.toastQueue.push({
        label: 'Copied to clipboard',
        group: CLIPBOARD_TOASTS_GROUP,
      });
    };
    return (
      <>
        <FigmaPasteHowToDialog selectionActions={selectionActions} />
        <WebflowPasteHowToDialog selectionActions={selectionActions} />
        <BoundWebflowSafariWarningDialog selectionActions={selectionActions} />
        <PasteViaShortcutsHowTo selectionActions={selectionActions} />
        {selectionActions.showReactExportHowTo && (
          <ExportReactDialog
            onClose={action(
              () => (selectionActions.showReactExportHowTo = false),
            )}
            showCopyConfirmation={showCopyConfirmation}
          />
        )}
        {selectionActions.showHtmlExportHowTo && (
          <ExportHtmlDialog
            onClose={action(
              () => (selectionActions.showHtmlExportHowTo = false),
            )}
          />
        )}
      </>
    );
  },
);

const FigmaPasteHowToDialog = observer(function _FPHTDialog({
  selectionActions,
}: {
  selectionActions: SelectionActionsImpl;
}) {
  const [dismiss, setDismiss] = React.useState(false);
  const handleClose = React.useCallback(() => {
    dismiss && setDismissedFigmaPasteHowTo();
    selectionActions.showFigmaPasteHowTo = false;
  }, [dismiss, selectionActions]);

  if (!selectionActions.showFigmaPasteHowTo) return null;
  return (
    <Dialog
      title="Paste into Figma Plugin"
      onClose={handleClose}
      buttons={
        <>
          <div className={styles.dialogCheckboxContainer}>
            <Checkbox
              label="Don't show me this again"
              onChange={setDismiss}
              value={dismiss}
              size="large"
            />
          </div>
          <Button
            label="Got it"
            onClick={handleClose}
            size="large"
            variant="primary"
          />
        </>
      }
    >
      <DialogHeroImage
        src={ExportFigmaPasteImage}
        aspectRatio="864/547"
        alt="Screenshot of the Relume Figma plugin"
      />
      <TextRegularLight>
        Before pasting into Figma, make sure your project was created by
        duplicating the Relume Figma Kit and you have the Relume Plugin
        installed.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow type="one">
          <div className={styles.textAndButtonContainer}>
            Open the Relume Figma Kit.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                icon={IconFigma}
                label="Open in Figma"
                href="https://www.figma.com/community/file/1078092050664989246"
                target="_blank"
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="two">
          <div className={styles.textAndButtonContainer}>
            Install the Relume Figma Plugin.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                icon={IconFigma}
                label="Install plugin"
                href="https://www.figma.com/community/plugin/1245615905217691936/Relume-Library"
                target="_blank"
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="three">Select ‘Paste from Site Builder’.</TickRow>
      </Rows>
    </Dialog>
  );
});

const WebflowPasteHowToDialog = observer(function _WPHTDialog({
  selectionActions,
}: {
  selectionActions: SelectionActionsImpl;
}) {
  const [dismiss, setDismiss] = React.useState(false);
  const handleClose = React.useCallback(() => {
    dismiss && setDismissedWebflowPasteHowTo();
    selectionActions.showWebflowPasteHowTo = false;
  }, [dismiss, selectionActions]);

  if (!selectionActions.showWebflowPasteHowTo) return null;
  return (
    <Dialog
      title="Paste into Style Guide"
      onClose={handleClose}
      buttons={
        <>
          <div className={styles.dialogCheckboxContainer}>
            <Checkbox
              label="Don't show me this again"
              onChange={setDismiss}
              value={dismiss}
              size="large"
            />
          </div>
          <Button
            label="Got it"
            onClick={handleClose}
            size="large"
            variant="primary"
          />
        </>
      }
    >
      <DialogHeroImage
        src={ExportWebflowPasteImage}
        aspectRatio="864/547"
        alt="Screenshot of the Relume Style Guide in Webflow Designer"
      />
      <TextRegularLight>
        Before pasting into Webflow, make sure your project was created by
        cloning the Relume Style Guide and you have the Chrome Extension
        installed to sync classes.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow type="one">
          <div className={styles.textAndButtonContainer}>
            Clone the Relume Style Guide for Webflow.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                onClick={() => {
                  return window.open(
                    'https://webflow.com/made-in-webflow/website/relume-library-styleguide',
                    '_blank',
                  );
                }}
                label="Clone Style Guide"
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="two">
          <div className={styles.textAndButtonContainer}>
            Install the Relume Chrome Extension.
            <div className={styles.installWebflowAppButtonContainer}>
              <Button
                label="Install Chrome Extension"
                onClick={() => {
                  return window.open(
                    'https://chromewebstore.google.com/detail/relume-chrome-extension-f/doeokejknjdlpgkkmlbcahojmnpdlebm',
                    '_blank',
                  );
                }}
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="three">Paste into your Webflow project.</TickRow>
      </Rows>
    </Dialog>
  );
});

const BoundWebflowSafariWarningDialog = observer(function _BWSWDialog({
  selectionActions,
}: {
  selectionActions: SelectionActionsImpl;
}) {
  const handleClose = React.useCallback(() => {
    selectionActions.showWebflowSafariWarning = false;
  }, [selectionActions]);
  if (!selectionActions.showWebflowSafariWarning) return null;
  return <WebflowSafariWarningDialog onClose={handleClose} />;
});

const PasteViaShortcutsHowTo = observer(function _WPHTDialog({
  selectionActions,
}: {
  selectionActions: SelectionActionsImpl;
}) {
  const handleClose = React.useCallback(() => {
    selectionActions.showPasteViaShortcutsHowTo = false;
  }, [selectionActions]);

  if (!selectionActions.showPasteViaShortcutsHowTo) return null;
  return (
    <Dialog
      title="How to paste"
      onClose={handleClose}
      buttons={
        <Button
          label="Got it"
          onClick={handleClose}
          size="large"
          variant="primary"
        />
      }
    >
      <TextRegularLight>
        Your browser doesn't allow pasting via the menu. Please use the keyboard
        shortcuts instead.
      </TextRegularLight>
      <Title5>{isMac ? '⌘' : 'Ctrl'} + V</Title5>
    </Dialog>
  );
});
